import React, { useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { useHotel } from "../contexts/HotelContext";
import { useToast } from "../contexts/ToastContext";
import { EmptyState } from "../components/common/EmptyState";
import { ErrorState } from "../components/common/ErrorState";
import { StatusBadge } from "../components/common/StatusBadge";
import { formatDate } from "../utils/date-format";
import {
  getProcessingErrors,
  resolveProcessingError,
  ProcessingErrorDto,
  ProcessingErrorsResponse,
  ProcessingErrorType,
} from "../services/grantsApi";

/**
 * Polish labels for processing error types.
 * @see docs/specifications/2026-03-11-processing-pipeline-error-monitoring.md
 */
const ERROR_TYPE_LABELS: Record<ProcessingErrorType, string> = {
  PAYLOAD_TOO_LARGE: "Zbyt duży artykuł",
  CONTENT_FETCH_FAILED: "Błąd pobierania",
  ZOD_VALIDATION_FAILED: "Błąd walidacji",
  CONTEXT_RESOLUTION_FAILED: "Brak kontekstu",
  ARTICLE_NOT_FOUND: "Artykuł nie znaleziony",
  LINK_NOT_FOUND: "Link nie znaleziony",
  UNKNOWN: "Nieznany",
};

/**
 * Maps processing error types to StatusBadge status types.
 * PAYLOAD_TOO_LARGE is a content issue (amber), UNKNOWN is gray, rest are errors (red).
 */
const ERROR_TYPE_STATUS: Record<ProcessingErrorType, "pending" | "error" | "unknown"> = {
  PAYLOAD_TOO_LARGE: "pending",
  CONTENT_FETCH_FAILED: "error",
  ZOD_VALIDATION_FAILED: "error",
  CONTEXT_RESOLUTION_FAILED: "error",
  ARTICLE_NOT_FOUND: "error",
  LINK_NOT_FOUND: "error",
  UNKNOWN: "unknown",
};

/**
 * Error type options for filter dropdown.
 */
const ERROR_TYPE_OPTIONS: Array<{ value: ProcessingErrorType; label: string }> =
  Object.entries(ERROR_TYPE_LABELS).map(([value, label]) => ({
    value: value as ProcessingErrorType,
    label,
  }));

/**
 * Resolved filter options.
 */
const RESOLVED_OPTIONS = [
  { value: "", label: "Wszystkie" },
  { value: "false", label: "Nierozwiązane" },
  { value: "true", label: "Rozwiązane" },
];

const PAGE_SIZE = 50;

/**
 * Admin page for monitoring processing pipeline errors.
 * Shows list of errors with filtering and resolve action.
 */
const GrantsProcessingErrors: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Mirror ModeratorRoute: global admin OR kiedynabory moderator module.
  const canModerate =
    user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");

  // Filter state from URL
  const sourceId = searchParams.get("sourceId") || "";
  const errorType = searchParams.get("errorType") || "";
  const resolved = searchParams.get("resolved") ?? "false";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const offset = (currentPage - 1) * PAGE_SIZE;

  useEffect(() => {
    document.title = "Błędy pipeline - KiedyNabory.pl";
  }, []);

  // Fetch processing errors list
  const { data, isLoading, error, refetch } =
    useQuery<ProcessingErrorsResponse>({
      queryKey: [
        "processing-errors",
        sourceId,
        errorType,
        resolved,
        currentPage,
      ],
      queryFn: () =>
        getProcessingErrors({
          sourceId: sourceId || undefined,
          errorType: errorType || undefined,
          resolved: resolved || undefined,
          limit: PAGE_SIZE,
          offset,
        }),
    });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: (id: string) => resolveProcessingError(id),
    onSuccess: () => {
      addToast({
        message: "Błąd oznaczony jako rozwiązany",
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["processing-errors"] });
      queryClient.invalidateQueries({
        queryKey: ["processing-errors-summary"],
      });
    },
    onError: (err: Error) => {
      addToast({
        message: `Nie udało się rozwiązać błędu: ${err.message}`,
        type: "error",
      });
    },
  });

  // Filter change handlers
  const handleErrorTypeChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("errorType", value);
      } else {
        newParams.delete("errorType");
      }
      newParams.set("page", "1");
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleResolvedChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("resolved", value);
      } else {
        newParams.delete("resolved");
      }
      newParams.set("page", "1");
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", page.toString());
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const hasFiltersActive = !!(sourceId || errorType || (resolved && resolved !== "false"));

  if (!canModerate) {
    return (
      <Layout>
        <div className="p-6 text-center text-red-600">
          Brak uprawnień do tej strony
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <nav className="text-sm text-neutral-500">
          <Link to="/grants/calls" className="hover:text-primary-600">
            Nabory
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="text-neutral-900">Błędy pipeline</span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Błędy pipeline
            </h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Monitorowanie błędów w procesie analizy artykułów
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label
              htmlFor="errorType-filter"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Typ błędu
            </label>
            <select
              id="errorType-filter"
              value={errorType}
              onChange={(e) => handleErrorTypeChange(e.target.value)}
              className="w-48 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Wszystkie typy</option>
              {ERROR_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="resolved-filter"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Status
            </label>
            <select
              id="resolved-filter"
              value={resolved}
              onChange={(e) => handleResolvedChange(e.target.value)}
              className="w-48 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {RESOLVED_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {hasFiltersActive && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 rounded-md"
            >
              Wyczyść filtry
            </button>
          )}

          {sourceId && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary-100 text-primary-800">
              Źródło: {sourceId.substring(0, 8)}...
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete("sourceId");
                  newParams.set("page", "1");
                  setSearchParams(newParams, { replace: true });
                }}
                className="ml-1 hover:text-primary-700"
                aria-label="Usuń filtr źródła"
              >
                ×
              </button>
            </span>
          )}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200" aria-busy={isLoading}>
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie błędów...
              </p>
            </div>
          ) : error ? (
            <ErrorState
              title="Błąd ładowania"
              message={(error as Error).message || "Nieznany błąd"}
              onRetry={() => refetch()}
            />
          ) : data?.data && data.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                    >
                      Typ błędu
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                    >
                      Komunikat
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase"
                    >
                      Wystąpienia
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                    >
                      Ostatnie wystąpienie
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase"
                    >
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {data.data.map((item) => (
                    <ProcessingErrorRow
                      key={item.id}
                      error={item}
                      onResolve={(id) => resolveMutation.mutate(id)}
                      isResolving={resolveMutation.isPending}
                      resolvingId={
                        resolveMutation.isPending
                          ? (resolveMutation.variables as string)
                          : null
                      }
                    />
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between">
                  <div className="text-sm text-neutral-500">
                    Strona {currentPage} z {totalPages} ({data?.total || 0}{" "}
                    błędów)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="px-3 py-1 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Poprzednia
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <EmptyState
              title={
                hasFiltersActive
                  ? "Brak wyników"
                  : "Brak błędów — pipeline działa poprawnie"
              }
              description={
                hasFiltersActive
                  ? "Żadne błędy nie pasują do wybranych filtrów."
                  : undefined
              }
              action={
                hasFiltersActive ? (
                  <button
                    onClick={handleClearFilters}
                    className="mt-2 text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Wyczyść filtry
                  </button>
                ) : undefined
              }
              className="p-6"
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

/**
 * Table row for a single processing error.
 */
interface ProcessingErrorRowProps {
  error: ProcessingErrorDto;
  onResolve: (id: string) => void;
  isResolving: boolean;
  resolvingId: string | null;
}

const ProcessingErrorRow: React.FC<ProcessingErrorRowProps> = ({
  error: item,
  onResolve,
  isResolving,
  resolvingId,
}) => {
  const isThisResolving = isResolving && resolvingId === item.id;
  const isResolved = !!item.resolvedAt;

  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-4 py-4">
        <StatusBadge
          status={ERROR_TYPE_STATUS[item.errorType] || "unknown"}
          label={ERROR_TYPE_LABELS[item.errorType] || item.errorType}
        />
      </td>
      <td className="px-4 py-4">
        <div
          className="text-sm text-neutral-900 max-w-md truncate text-left"
          title={item.errorMessage}
        >
          {item.errorMessage}
        </div>
        {item.sourceId && (
          <div className="text-xs text-neutral-500 mt-1 text-left">
            {"Źródło"}: {" "}
            <Link
              to={`/grants/sources?highlight=${item.sourceId}`}
              className="text-primary-600 hover:underline"
            >
              {item.sourceId.substring(0, 8)}...
            </Link>
          </div>
        )}
      </td>
      <td className="px-4 py-4 text-right">
        <span className="text-sm font-medium text-neutral-900">
          {item.occurrenceCount}
        </span>
      </td>
      <td className="px-4 py-4 text-sm text-neutral-500 text-left">
        {formatDate(item.lastOccurredAt)}
      </td>
      <td className="px-4 py-4 text-center">
        {isResolved ? (
          <>
            <StatusBadge status="success" label="Rozwiązany" />
            {item.resolvedAt && (
              <span className="text-xs text-gray-500 block mt-1">
                {formatDate(item.resolvedAt)}
              </span>
            )}
          </>
        ) : (
          <StatusBadge status="pending" label="Nierozwiązany" />
        )}
      </td>
      <td className="px-4 py-4 text-right">
        {!isResolved && (
          <button
            onClick={() => {
              if (!window.confirm('Czy na pewno chcesz oznaczyć ten błąd jako rozwiązany? Operacja jest nieodwracalna.')) return;
              onResolve(item.id);
            }}
            disabled={isThisResolving}
            aria-label={`Rozwiąż błąd: ${ERROR_TYPE_LABELS[item.errorType]} (${formatDate(item.lastOccurredAt)})`}
            className={`inline-flex items-center text-sm font-medium rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              isThisResolving
                ? "text-neutral-400 cursor-wait"
                : "text-primary-600 hover:text-primary-800 hover:bg-primary-50"
            }`}
          >
            {isThisResolving ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></span>
                Rozwiązywanie...
              </>
            ) : (
              "Rozwiąż"
            )}
          </button>
        )}
      </td>
    </tr>
  );
};

export default GrantsProcessingErrors;
